// libraries
import dayjs from 'dayjs'
import React from 'react'

// globals
import API from './API.js'
import Events from '@st-operations/uikit/Events.js'
import Storage from '@st-operations/uikit/Storage.js'
import Team from './Team.js'

// libraries
import Cookies from './cookies.js'
import getQueryParam from '@st-operations/uikit/lib/getQueryParam.js'

// constants
const NON_AUTH_PAGES = [
  'login',
  'signup',
  'recover-password',
  'forgot-password',
  'accept-invitation',
  'view-only',
]
const TEAM_ID = require('@st-operations/uikit/constants/sharethis_team_id.js').default

// class
let User = {}

User.acceptInvitation = function (invitation_token, team_id, next) {
  if (!next) {
    next = function () {}
  }
  window._sso_api.get(
    '/accept-invitation',
    {
      invitation_token,
      team_id,
    },
    (err, resp) => {
      if (err) {
        return next(err)
      }

      User.setTokens(resp.tokens)
      User.refresh(next)
    }
  )
}

User.connectToFacebook = function () {
  window.mixpanel.track('UIKit - Connected Facebook Account')
  return window._sso_api.redirect('/facebook/connect')
}

User.connectTo = function (service) {
  window.mixpanel.track(`UIKit - Connected to ${account} Account`)
  let platform_token = Cookies.getCookie('platform_token')
  window._sso_api.redirect(`/${service}/connect`, { platform_token })
  let account = service[0].toUpperCase() + service.slice(1)
  return account
}

User.checkLocalStorageForToken = function () {
  let pathname = window.location.pathname
  let page = pathname.split('/')[1]

  // If in old subdomain redirect to new one
  if (window.location.host === 'studio.sharethis.com') {
    window.location.replace(`https://datasphere.sharethis.com${pathname}`)
  }

  let token = Cookies.getCookie('token')
  if (token) {
    User.setToken(token)
    User.refresh((err, user) => {
      // if user is logged in
      if (user) {
        // update mixpanel
        if (window.mixpanel) {
          window.mixpanel.track('UIKit - Auto Logged In')
        }

        // if user is logged in and on a non-auth page, load the dashboard page
        if (NON_AUTH_PAGES.indexOf(page) > -1) {
          let next = getQueryParam('next')
          if (next) {
            _history.push(next)
          } else {
            _history.push('/')
          }
        }
      }
    })
  }

  // if user is not logged in
  else {
    // TODO: Improve this logic
    Storage.set('pathname', pathname)
    // navigate to accept-invitation page
    if (page === 'accept-invitation') {
      _history.push('/accept-invitation')
      // navigate to forgot-password page
    } else if (page === 'forgot-password') {
      _history.push('/forgot-password')
      // navigate to recover password page
    } else if (page === 'recover-password') {
      _history.push('/recover-password')
      // navigate to login or signup page
    } else if (page.includes('view-only') || page === 'reset-password') {
      return
    } else if (['login', 'signup'].indexOf(page) == -1) {
      _history.push('/login')
    }
  }
}

User.forgotPassword = function (email, domain, next) {
  if (!next) {
    next = function () {}
  }
  window._sso_api.post(
    '/forgot-password',
    {
      email,
      domain,
    },
    (err, resp) => {
      if (err) {
        return next(err)
      }
      return next(null, resp)
    }
  )
}

User.getCookie = function (name) {
  let value = '; ' + document.cookie
  let parts = value.split(`; ${name}=`)
  if (parts.length == 2) {
    return parts.pop().split(';').shift()
  }
  return null
}

User.getGoogleAnalyticsId = function () {
  if (window.ga && ga.getAll()[0] && ga.getAll()[0].get('clientId')) {
    return ga.getAll()[0].get('clientId')
  }
  return null
}

User.getMixpanelDistinctId = function () {
  let cookie = User.getCookie('mp_eb4c820161f6ead8054c94e14c144900_mixpanel')
  let payload = JSON.parse(decodeURIComponent(cookie))
  if (payload) {
    return payload.distinct_id
  }
  return null
}

User.getTeamInfo = function (team_id, next) {
  if (!next) {
    next = function () {}
  }
  window._sso_api.get(
    '/team',
    {
      id: team_id,
    },
    (err, resp) => {
      if (err) {
        return next(err)
      }
      next(null, resp)
    }
  )
}

User.getUTMParams = function () {
  let cookie = User.getCookie('mp_eb4c820161f6ead8054c94e14c144900_mixpanel')
  let payload = JSON.parse(decodeURIComponent(cookie))
  if (payload) {
    return payload
  }
  return null
}

User.hasPermission = function (item, resource) {
  let user = User.data
  if (user && user.acl && user.acl[TEAM_ID]) {
    let role = user.acl[TEAM_ID][resource]
    if (item && item.created_by && item.created_by._id == user._id) {
      return true
    }
    if (['admin', 'operations'].includes(role)) {
      return true
    }
    return false
  }
  return false
}

User.init = function (config) {
  User.config = config
  User.checkLocalStorageForToken()
  User.checkQueryStringForToken()
}

User.checkQueryStringForToken = function (next) {
  let { pathname, search } = window.location
  if (search.indexOf('&') != -1) {
    let parameters = search.split('&')
    var page = parameters[1].split('=')[1]
    search = parameters[0]
  }
  let matches = /[\?\&]token=([0-9a-zA-Z_\.\-]+)/.exec(search)
  let token = matches ? matches[1] : null
  if (token) {
    // clear the pevious session if token found
    Storage.clear()
    User.setToken(token)
    User.refresh((err, user) => {
      // if user is logged in
      if (user) {
        // update mixpanel
        if (window.mixpanel) {
          window.mixpanel.track('UIKit - Auto Logged In')
        }
        if (page) {
          _history.push('/' + page)
        } else {
          _history.push('/')
        }
      }
    })
  }
}

User.identify = function (user) {
  // mixpanel
  if (window.mixpanel) {
    window.mixpanel.identify(user._id)
  }

  // appcues
  if (window.Appcues) {
    window.Appcues.identify(user._id, {
      email: user.email,
      last_login: new Date(),
      registered_at: new Date(user.registered_at),
      verified: user.verified,
    })
  }

  // autopilot
  if (window.Autopilot) {
    window.Autopilot.run('associate', {
      Email: user.email,
      custom: {
        'date--UIKit--Last--Logged--In': dayjs().format('YYYY-MM-DDThh:mm:ssZ'),
      },
    })
  }

  // segmentio
  if (window.analytics && window.analytics.identify) {
    window.analytics.identify(user._id, {
      email: user.email,
      estid: User.getCookie('__stid'),
      name: user.email,
    })
  }
}

User.login = function (email, password, next) {
  if (!next) {
    next = function () {}
  }
  window._sso_api.post('/login', { email, password }, (err, resp) => {
    if (err) {
      next(err)
    } else {
      User.setTokens(resp)
      User.refresh(next)
    }
  })
}

User.loginOrRegister = function (email, password, custom, next) {
  if (!next) {
    next = function () {}
  }
  window._sso_api.post('/login', { email, password }, (err, resp) => {
    if (/Invalid password/.test(err)) {
      next('account already exists, but password was incorrect')
    } else if (/No user with that address/.test(err)) {
      User.register(next)
    } else {
      User.setTokens(resp)
      User.refresh(next)
    }
  })
}

User.logout = function (user) {
  // send token to backend for invalidation
  window._sso_api.post('/logout', User.data, (err, resp) => {
    if (err) return err
  })

  Storage.clear({
    except: [
      'language',
      'do_not_show_wizard',
      'do_not_show_onboarding',
      'ListView.state',
      'ListView.custom-audiences.state',
      '@sharethis/ab-custom-audiences/filters',
      '@sharethis/custom-audiences/filters',
      '@sharethis/custom-taxonomies/filters',
      '@sharethis/event-feeds/filters',
      '@sharethis/pixel-audiences/filters',
      '@sharethis/taxonomies/filters',
      '@sharethis/white-label-audiences/filters',
    ],
  })
  User.setToken(null)
  User.data = null
  Events.emit('user', null)

  const allTokens = ['token', 'platform_token', 'dmp_token']
  allTokens.forEach((token) => {
    Cookies.removeCookie(token)
  })

  window.location.href = '/login'

  // close mixpanel
  if (window.mixpanel) {
    window.mixpanel.track('UIKit - Logged Out')
    window.mixpanel.reset()
  }

  // autopilot
  if (window.Autopilot) {
    window.Autopilot.run('associate', {
      Email: user.email,
      custom: {
        'boolean--UIKit--logged--in': false,
      },
    })
  }
}

User.refresh = function (next) {
  if (!next) {
    next = function () {}
  }
  window._sso_api.get('/self', (err, user) => {
    if (err) {
      return next(err)
    }
    User.data = user
    Events.emit('user', user)

    if (user) {
      User.identify(user)

      if (window.mixpanel) {
        window.mixpanel.track('UIKit - Logged In')
      }
      if (window.Appcues) {
        window.Appcues.track('Login')
      }
      if (window.Autopilot) {
        window.Autopilot.run('associate', {
          Email: user.email,
          custom: {
            'boolean--UIKit--logged--in': true,
          },
        })
      }
      if (window.analytics) {
        window.analytics.page(window.location.pathname)
      }

      // set team to be the selected team or first in all teams
      let team = user.teams[0]
      let selected_team = Storage.get('selected-team')
      if (selected_team) {
        team = user.teams.find((t) => {
          return t._id === selected_team.value
        })
        team = team || user.teams[0]
      }
      Team.set(team)
      if (team) {
        window._sso_api.get(`/team/${team._id}`, (err, team) => {
          Team.set(team)
        })
      }
    }

    // finish
    next(null, user)
  })
}

User.register = function (params, next) {
  if (!next) {
    next = function () {}
  }
  let ga_id = User.getGoogleAnalyticsId()
  let mixpanel_id = User.getMixpanelDistinctId()
  let utm = User.getUTMParams()
  let body = {
    email: params.email,
    ga_id,
    invitation_token: params.token,
    mixpanel_id,
    password: params.password,
    team_id: params.team_id,
    utm,
  }
  window._sso_api.post('/register', body, (err, resp) => {
    if (err) {
      return next(err)
    }

    User.setTokens(resp)
    User.refresh(next)
  })
}

User.removeCollaborator = function (email, team_id, next) {
  if (!next) {
    next = function () {}
  }
  window._sso_api.post('/team/remove', { email, team_id }, (err, resp) => {
    if (err) {
      return next(err)
    }
    next(null, resp)
  })
}

User.resetPassword = function (code, password, next) {
  if (!next) {
    next = function () {}
  }
  window._sso_api.post(
    '/reset-password',
    {
      code,
      password,
    },
    next
  )
}

User.setToken = function (token, next) {
  if (window.api) {
    window.api.token = token
  }
  if (window._api) {
    window._api.token = token
  }
  window._sso_api.token = token
  User.token = token
  Cookies.setCookie('token', token)
  Events.emit('token', token)
}

User.setTokens = function (tokens, next) {
  for (let token in tokens) {
    window._sso_api[token] = tokens[token]
    window._api[token] = tokens[token]
    User[token] = tokens[token]
    Cookies.setCookie(token, tokens[token])
    Events.emit(token, tokens[token])
  }
  if (window._dmp_api && tokens.dmp_token) {
    window._dmp_api.token = tokens.dmp_token
  }
}

User.sendInvite = function (body, next) {
  if (!next) {
    next = function () {}
  }
  window._sso_api.post('/send-invitation', body, (err, resp) => {
    if (err) {
      return next(err)
    }
    next(null, resp)
  })
}

User.getProfile = function () {
  return User.data
}

User.updatePassword = function (password, next) {
  if (!next) {
    next = function () {}
  }
  window._sso_api.post(
    '/reset-password-with-token',
    {
      password,
    },
    (err, resp) => {
      if (err) {
        return next(err)
      }
      next(resp)
    }
  )
}

User.updateProfile = function (name, phone_number, next) {
  if (!next) {
    next = function () {}
  }
  window._sso_api.post(
    '/update-profile',
    {
      name,
      phone_number,
    },
    (err, resp) => {
      if (err) {
        return next(err)
      }
      next(resp)
    }
  )
}

User.updateUserPermission = function (roles, team_id, user_id, next) {
  if (!next) {
    next = function () {}
  }
  window._sso_api.put('/team/acl', { roles, team_id, user_id }, (err, resp) => {
    next(err, resp)
  })
}

User.watch = function (component, callback) {
  let user = User.data

  if (component) {
    component.setState({ user }, () => {
      if (callback) {
        callback(user)
      }
    })
  } else {
    callback(user)
  }

  Events.addListener('user', (user) => {
    if (component) {
      component.setState({ user }, () => {
        if (callback) {
          callback(user)
        }
      })
    } else {
      callback(user)
    }
  })
}

// export
export default User
